import { useAuthStore } from "@features/auth/presentation/store/authStore";
import type { ChatMessage, ChatSummary } from "@features/chats/domain/types";
import { useWSEvents } from "@shared/hooks/useWebSocket";
import { playReceiveSound } from "@shared/lib/chatSounds";
import { wsClient } from "@shared/services/ws/wsClient";
import { useSelectedAccountStore } from "@shared/store/selectedAccountStore";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef } from "react";

/** Request browser notification permission once on mount. */
function useNotificationPermission() {
	const requested = useRef(false);
	useEffect(() => {
		if (
			!requested.current &&
			"Notification" in window &&
			Notification.permission === "default"
		) {
			requested.current = true;
			Notification.requestPermission();
		}
	}, []);
}

function showDesktopNotification(title: string, body: string) {
	if (!("Notification" in window) || Notification.permission !== "granted")
		return;
	if (document.visibilityState === "visible") return; // tab is focused — no need
	new Notification(title, { body, icon: "/favicon.ico" });
}

// Shape of data sent by the Go backend on message.incoming (PascalCase — no json tags)
interface IncomingMsgPayload {
	MessageID: string;
	ChatJID: string;
	SenderJID: string;
	Text: string;
	Caption: string;
	Timestamp: number;
	PushName: string;
	MediaType: string;
	MediaURL: string;
	MimeType: string;
	FileName: string;
}


interface PictureReadyPayload {
	jid: string;
	picture_id: string;
	url: string;
}

// Shape of data sent by the Go backend on message.edited
interface EditedMsgPayload {
	message_id?: string;
	chat_jid?: string;
	text?: string;
	is_edited?: boolean;
	edited_at?: number;
	MessageID?: string;
	ChatJID?: string;
	Text?: string;
	IsEdited?: boolean;
	EditedAt?: number;
}



export function WebSocketProvider({ children }: { children: ReactNode }) {
	const user = useAuthStore((s) => s.user);
	const qc = useQueryClient();
	useNotificationPermission();

	// Wire auth guard once so reconnect never fires without a session.
	useEffect(() => {
		wsClient.setAuthGuard(() => !!useAuthStore.getState().user);
	}, []);

	// Connect / disconnect with auth lifecycle
	useEffect(() => {
		if (user) {
			wsClient.connect();
		} else {
			wsClient.disconnect();
		}
	}, [user]);

	// Real-time cache updates
	useWSEvents((evt) => {
		switch (evt.type) {
			case "account.connected":
			case "account.disconnected":
				qc.invalidateQueries({ queryKey: ["accounts"] });
				// Aggregated health/accounts dashboard reflects connect counts.
				qc.invalidateQueries({ queryKey: ["analytics", "health"] });
				if (evt.account_id) {
					qc.invalidateQueries({
						queryKey: ["account-status", evt.account_id],
					});
					// Sessions list query key is `["accounts","sessions",id,params]` —
					// invalidate the prefix so any param variant gets refreshed.
					qc.invalidateQueries({
						queryKey: ["accounts", "sessions", evt.account_id],
					});
					qc.invalidateQueries({
						queryKey: ["account-health", evt.account_id],
					});
					// Once the device is paired, the QR endpoint returns 409.
					// removeQueries cancels the in-flight refetchInterval immediately
					// — no waiting for the next render to re-evaluate `enabled`.
					if (evt.type === "account.connected") {
						qc.removeQueries({ queryKey: ["account-qr", evt.account_id] });
					}
				}
				break;

			case "message.incoming": {
				if (!evt.account_id) break;
				const raw = evt.data as IncomingMsgPayload | undefined;
				if (!raw?.ChatJID) {
					// fallback: invalidate all filter variants
					qc.invalidateQueries({ queryKey: ["chats", evt.account_id] });
					break;
				}

				const text = raw.Text || raw.Caption || "";
				const ts = new Date(raw.Timestamp * 1000).toISOString();

				// Reproducir sonido y notificación del navegador solo si el mensaje es de la cuenta activa
				const selectedAccountId = useSelectedAccountStore.getState().accountId;
				const isCurrentAccount = !selectedAccountId || selectedAccountId === evt.account_id;

				if (isCurrentAccount) {
					playReceiveSound();

					showDesktopNotification(
						raw.PushName || raw.SenderJID,
						text || (raw.MediaType ? `[${raw.MediaType}]` : "Nuevo mensaje"),
					);
				}

				// 1. Push message into open conversation cache (instant UI update, no refetch)
				qc.setQueryData<ChatMessage[]>(
					["chat-messages", evt.account_id, raw.ChatJID],
					(old) => {
						if (!old) return old; // conversation not loaded yet — skip
						if (old.some((m) => m.message_id === raw.MessageID)) return old; // dedupe
						const newMsg: ChatMessage = {
							id: raw.MessageID,
							message_id: raw.MessageID,
							chat_jid: raw.ChatJID,
							sender_jid: raw.SenderJID,
							from_me: false,
							text,
							push_name: raw.PushName,
							timestamp: ts,
							timestamp_ts: raw.Timestamp,
							media_type: raw.MediaType || undefined,
							media_url: raw.MediaURL || undefined,
							media_mime: raw.MimeType || undefined,
							media_filename: raw.FileName || undefined,
							source: "incoming",
						};
						return [...old, newMsg];
					},
				);

				// 2. Update chat summary row in the "all" list (bump last_message + last_time)
				qc.setQueryData<ChatSummary[]>(
					["chats", evt.account_id, "all"],
					(old) => {
						if (!old) return old;
						const exists = old.some((c) => c.chat_jid === raw.ChatJID);
						if (exists) {
							return old.map((c) =>
								c.chat_jid === raw.ChatJID
									? {
										...c,
										last_message: text,
										last_time: ts,
										last_time_ts: raw.Timestamp,
										from_me: false,
										last_source: "incoming" as const,
									}
									: c,
							);
						}
						// New chat not yet in list → invalidate all chat filter variants
						qc.invalidateQueries({ queryKey: ["chats", evt.account_id] });
						return old;
					},
				);

				// 3. Analytics counters still need a refetch (aggregated server-side)
				qc.invalidateQueries({ queryKey: ["analytics", "global"] });
				qc.invalidateQueries({
					queryKey: ["analytics", "account", evt.account_id],
				});
				break;
			}

			case "message.edited": {
				if (!evt.account_id) break;
				const raw = evt.data as EditedMsgPayload | undefined;
				const messageId = raw?.message_id || raw?.MessageID;
				const chatJid = raw?.chat_jid || raw?.ChatJID;
				const text = raw?.text ?? raw?.Text ?? "";
				const isEdited = raw?.is_edited ?? raw?.IsEdited ?? true;
				const editedAt = raw?.edited_at ?? raw?.EditedAt ?? Math.floor(Date.now() / 1000);

				if (!chatJid || !messageId) break;

				// 1. In-place update in open conversation cache (no duplicate, no scroll jump)
				qc.setQueriesData<ChatMessage[]>(
					{ queryKey: ["chat-messages", evt.account_id] },
					(old) => {
						if (!old) return old;
						return old.map((m) =>
							m.message_id === messageId || m.id === messageId || m.id === `ws-${messageId}`
								? {
									...m,
									text,
									is_edited: isEdited,
									edited_at: editedAt,
								}
								: m,
						);
					},
				);

				// 2. Update chat preview snippet if this message is the latest in that chat
				qc.setQueriesData<ChatSummary[]>(
					{ queryKey: ["chats", evt.account_id] },
					(old) => {
						if (!old) return old;
						return old.map((c) => {
							const isMatch =
								c.chat_jid === chatJid ||
								(chatJid &&
									c.chat_jid.split("@")[0] ===
									chatJid.split("@")[0]);
							if (!isMatch) return c;
							return {
								...c,
								last_message: text,
							};
						});
					},
				);
				break;
			}

			case "message.delivered":
			case "message.read":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["chat-messages", evt.account_id] });
					qc.invalidateQueries({ queryKey: ["chat-statuses", evt.account_id] });
					// Health includes delivery-rate-derived metrics.
					qc.invalidateQueries({
						queryKey: ["account-health", evt.account_id],
					});
					qc.invalidateQueries({ queryKey: ["analytics", "health"] });
				}
				break;

			case "message.failed":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["outbox", evt.account_id] });
					qc.invalidateQueries({ queryKey: ["analytics", "global"] });
					// error_rate / failed_today change on every failure.
					qc.invalidateQueries({
						queryKey: ["account-health", evt.account_id],
					});
					qc.invalidateQueries({ queryKey: ["analytics", "health"] });
				}
				break;

			case "message.sent":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["outbox", evt.account_id] });
					qc.invalidateQueries({ queryKey: ["analytics", "global"] });
					qc.invalidateQueries({
						queryKey: ["analytics", "account", evt.account_id],
					});
					qc.invalidateQueries({
						queryKey: ["account-health", evt.account_id],
					});
					qc.invalidateQueries({ queryKey: ["analytics", "health"] });
					// Refresh chat messages so sent message appears immediately in the conversation
					qc.invalidateQueries({ queryKey: ["chat-messages", evt.account_id] });
					qc.invalidateQueries({ queryKey: ["chats", evt.account_id] });
				}
				break;

			case "scheduled.sent":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["scheduled", evt.account_id] });
					qc.invalidateQueries({ queryKey: ["analytics", "global"] });
				}
				break;

			case "picture.ready": {
				// The backend's chat-list warmup resolved a JID's
				// profile picture in the background and is
				// publishing the CDN URL. Patch every chat-list
				// query cache variant (["chats", account, "all"],
				// ["chats", account, "bot"], etc.) so the avatar
				// swaps in the UI without a refetch.
				if (!evt.account_id) break;
				const raw = evt.data as PictureReadyPayload | undefined;
				if (!raw?.jid || !raw.url) break;
				const jid = raw.jid;
				const url = raw.url;
				const pictureId = raw.picture_id || "";
				// Patch all chat-list variants whose second key
				// segment is the account id. The third segment
				// is the filter ("all" | "bot" | "unread"); we
				// don't enumerate it because TanStack matches
				// by prefix.
				qc.setQueriesData<ChatSummary[]>(
					{ queryKey: ["chats", evt.account_id] },
					(old) => {
						if (!old) return old;
						return old.map((c) =>
							c.chat_jid === jid
								? {
									...c,
									picture_url: url,
									picture_id: pictureId || c.picture_id,
								}
								: c,
						);
					},
				);
				break;
			}

			case "reminder.sent":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["reminders", evt.account_id] });
				}
				break;

			case "scheduled.cancelled":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["scheduled", evt.account_id] });
				}
				break;

			case "reminder.cancelled":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["reminders", evt.account_id] });
				}
				break;

			case "outbox.updated":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["outbox", evt.account_id] });
				}
				break;

			case "template.created":
			case "template.updated":
			case "template.deleted":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["templates", evt.account_id] });
				}
				break;

			case "label.created":
			case "label.updated":
			case "label.deleted":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["labels", evt.account_id] });
				}
				break;

			case "group.created":
			case "group.updated":
				if (evt.account_id) {
					qc.invalidateQueries({ queryKey: ["groups", evt.account_id] });
				}
				break;

			case "auto-response.created":
			case "auto-response.updated":
			case "auto-response.deleted":
				if (evt.account_id) {
					qc.invalidateQueries({
						queryKey: ["auto-responses", evt.account_id],
					});
				}
				break;

			case "analytics.updated":
				qc.invalidateQueries({ queryKey: ["analytics"] });
				break;
		}
	});

	return <>{children}</>;
}
